import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import AsyncSelect from "react-select/async";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import clsx from "clsx";
import { useTable, usePagination } from "react-table";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisVertical,
  faEdit,
  faEye,
  faShieldAlt,
  faToggleOn,
  faToggleOff,
} from "@fortawesome/free-solid-svg-icons";
import { BASE_URL } from "../../config";
import PlanBadge from "../PlanBadge";
import PlanTypeInfoTooltip_v2 from "../PlanTypeInfoTooltip_v2";

const Badge = ({ children, color = "gray" }) => {
  const colors = {
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
    green: "bg-green-100 text-green-700 ring-green-200",
    blue: "bg-blue-100 text-blue-700 ring-blue-200",
    red: "bg-red-100 text-red-700 ring-red-200",
    yellow: "bg-yellow-100 text-yellow-700 ring-yellow-200",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
        colors[color]
      )}
    >
      {children}
    </span>
  );
};

const EmptyState = ({
  title = "No results",
  subtitle = "Try adjusting your search or filters.",
}) => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 p-10 text-center">
    <MagnifyingGlassIcon className="h-8 w-8 text-gray-400 mb-3" />
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
  </div>
);

const ACTION_MENU_WIDTH = 288;
const ACTION_MENU_HEIGHT = 220;
const ACTION_MENU_MARGIN = 12;
const ACTION_MENU_GAP = 8;

const getActionMenuPosition = (triggerElement) => {
  const rect = triggerElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = rect.right - ACTION_MENU_WIDTH;
  left = Math.max(
    ACTION_MENU_MARGIN,
    Math.min(left, viewportWidth - ACTION_MENU_WIDTH - ACTION_MENU_MARGIN)
  );

  let top = rect.bottom + ACTION_MENU_GAP;
  let placement = "bottom";

  if (top + ACTION_MENU_HEIGHT > viewportHeight - ACTION_MENU_MARGIN) {
    top = rect.top - ACTION_MENU_HEIGHT - ACTION_MENU_GAP;
    placement = "top";
  }

  const maxTop = Math.max(
    ACTION_MENU_MARGIN,
    viewportHeight - ACTION_MENU_HEIGHT - ACTION_MENU_MARGIN
  );
  top = Math.max(ACTION_MENU_MARGIN, Math.min(top, maxTop));

  return { top, left, placement };
};

const getPlanRowActionKey = (row) =>
  String(row?.corp_code || "") + "-" + String(row?.plan_code || "");

const planTypeLabels = {
  P: "P - Principal",
  p: "P - Principal",
  D: "D - Dependent",
  d: "D - Dependent",
  B: "B - Both",
  b: "B - Both",
};

const planLimitTypeLabels = {
  1: "MBL Only (Maximum Benefit Limit)",
  2: "ABL Only (Annual Benefit Limit)",
  3: "Both MBL & ABL",
};

const isBlankValue = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "");

const formatViewValue = (value) => {
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (isBlankValue(value)) {
    return "N/A";
  }

  return String(value);
};

const formatYesNo = (value) =>
  value === true || value === 1 || value === "1" ? "Yes" : "No";

const planViewFields = [
  { key: "corp_code", label: "Corp Code" },
  { key: "corp_name", label: "Corporate Name" },
  { key: "plan_code", label: "Plan Code" },
  { key: "plan_name", label: "Plan Name" },
  { key: "effective_date", label: "Effective Date" },
  { key: "expiry_date", label: "Expiry Date" },
  {
    key: "plan_type",
    label: "Plan Type",
    render: (value) => planTypeLabels[value] || value,
  },
  { key: "shared", label: "Shared", render: formatYesNo },
  { key: "preexist", label: "Pre-existing" },
  { key: "pec_dread", label: "Dreaded Diseases Covered", wide: true },
  {
    key: "plan_limit_type",
    label: "Plan Limit Type",
    render: (value) => planLimitTypeLabels[value] || value,
  },
  { key: "intervening_prd", label: "Intervening Period" },
  { key: "copay_corp", label: "CoPay c/o Company (%)" },
  { key: "copay_member", label: "CoPay c/o Member (%)" },
  { key: "exclusion1", label: "Other Exclusions/Modalities", wide: true },
  { key: "no_of_visits", label: "Total Number of Visit" },
  { key: "anes_basic_rate", label: "Anesthesia Basic Rate" },
  { key: "anes_major_rate", label: "Anesthesia Major Rate" },
  { key: "shared_dependent", label: "Shared Dependent", render: formatYesNo },
  { key: "ataccess_num", label: "ATAccess Count" },
  { key: "mpsyche_num", label: "MPsyche Count" },
  { key: "ftw", label: "Fit To Work", render: formatYesNo },
  { key: "rnb_amt", label: "Room & Board Amount" },
];

const DetailField = ({ label, value, wide = false }) => (
  <div
    className={clsx(
      "rounded-lg border border-gray-200 bg-white px-3 py-2",
      wide && "md:col-span-2 xl:col-span-3"
    )}
  >
    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </dt>
    <dd className="mt-1 whitespace-pre-wrap break-words text-sm font-medium text-gray-900">
      {formatViewValue(value)}
    </dd>
  </div>
);

const SectionTitle = ({ title, count }) => (
  <div className="mb-3 flex items-center justify-between">
    <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
      {title}
    </h3>
    {typeof count === "number" && (
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
        {count}
      </span>
    )}
  </div>
);

const PlanViewModal = ({ isOpen, loading, details, onClose }) => {
  const plan = details?.plan || {};
  const coverages = details?.coverages || [];
  const benefits = details?.benefits || [];
  const displayedPlanKeys = new Set(planViewFields.map((field) => field.key));
  const additionalPlanFields = Object.entries(plan).filter(
    ([key, value]) => !displayedPlanKeys.has(key) && !isBlankValue(value)
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9997] flex items-center justify-center bg-slate-950/60 p-4"
        >
          <motion.div
            initial={{ scale: 0.98, y: 16 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.98, y: 16 }}
            className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/10"
          >
            <div className="flex items-start justify-between border-b border-gray-200 bg-gradient-to-r from-slate-50 to-white px-5 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Plan Viewing
                </p>
                <h2 className="mt-1 text-lg font-semibold text-slate-950">
                  {formatViewValue(plan.plan_name)}
                </h2>
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
                  <span className="rounded-full bg-blue-50 px-2 py-1 font-semibold text-blue-700">
                    {formatViewValue(plan.corp_code)}
                  </span>
                  <span className="rounded-full bg-slate-100 px-2 py-1 font-semibold text-slate-700">
                    {formatViewValue(plan.plan_code)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Close plan viewing modal"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto px-5 py-4">
              {loading ? (
                <div className="space-y-4">
                  <Skeleton height={72} count={3} />
                  <Skeleton height={180} />
                </div>
              ) : (
                <div className="space-y-6">
                  <section>
                    <SectionTitle title="Plan Details" />
                    <dl className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {planViewFields.map((field) => (
                        <DetailField
                          key={field.key}
                          label={field.label}
                          value={
                            field.render
                              ? field.render(plan[field.key])
                              : plan[field.key]
                          }
                          wide={field.wide}
                        />
                      ))}
                    </dl>
                  </section>

                  <section>
                    <SectionTitle title="Coverages" count={coverages.length} />
                    {coverages.length ? (
                      <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                            <tr>
                              <th className="px-3 py-2 text-left">Coverage Code</th>
                              <th className="px-3 py-2 text-left">Plan Name</th>
                              <th className="px-3 py-2 text-left">Amount Limit</th>
                              <th className="px-3 py-2 text-left">Count Limit</th>
                              <th className="px-3 py-2 text-left">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 bg-white">
                            {coverages.map((coverage, index) => (
                              <tr key={`${coverage.coverage_code}-${index}`}>
                                <td className="px-3 py-2 font-medium text-slate-900">
                                  {formatViewValue(coverage.coverage_code)}
                                </td>
                                <td className="px-3 py-2 text-slate-700">
                                  {formatViewValue(coverage.plan_name)}
                                </td>
                                <td className="px-3 py-2 text-slate-700">
                                  {formatViewValue(coverage.amount_limit)}
                                </td>
                                <td className="px-3 py-2 text-slate-700">
                                  {formatViewValue(coverage.count_limit)}
                                </td>
                                <td className="px-3 py-2 text-slate-700">
                                  {formatViewValue(coverage.status_flag)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <EmptyState
                        title="No coverages found"
                        subtitle="No coverage data was returned for this plan."
                      />
                    )}
                  </section>

                  <section>
                    <SectionTitle title="Benefit Limits" count={benefits.length} />
                    {benefits.length ? (
                      <div className="max-h-96 overflow-auto rounded-lg border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                            <tr>
                              <th className="px-3 py-2 text-left">Coverage Code</th>
                              <th className="px-3 py-2 text-left">Benefit Code</th>
                              <th className="px-3 py-2 text-left">Benefit Description</th>
                              <th className="px-3 py-2 text-left">Basic Limit</th>
                              <th className="px-3 py-2 text-left">Major Limit</th>
                              <th className="px-3 py-2 text-left">R&B Type</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 bg-white">
                            {benefits.map((benefit, index) => (
                              <tr key={`${benefit.coverage_code}-${benefit.benefit_code}-${index}`}>
                                <td className="px-3 py-2 font-medium text-slate-900">
                                  {formatViewValue(benefit.coverage_code)}
                                </td>
                                <td className="px-3 py-2 text-slate-700">
                                  {formatViewValue(benefit.benefit_code)}
                                </td>
                                <td className="px-3 py-2 text-slate-700">
                                  {formatViewValue(benefit.benefit_desc)}
                                </td>
                                <td className="px-3 py-2 text-slate-700">
                                  {formatViewValue(benefit.basic_limit)}
                                </td>
                                <td className="px-3 py-2 text-slate-700">
                                  {formatViewValue(benefit.major_limit)}
                                </td>
                                <td className="px-3 py-2 text-slate-700">
                                  {formatViewValue(benefit.rnb_room_type)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <EmptyState
                        title="No benefits found"
                        subtitle="No benefit limit data was returned for this plan."
                      />
                    )}
                  </section>

                  {additionalPlanFields.length > 0 && (
                    <section>
                      <SectionTitle title="Additional Plan Data" />
                      <dl className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {additionalPlanFields.map(([key, value]) => (
                          <DetailField key={key} label={key} value={value} />
                        ))}
                      </dl>
                    </section>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const initialTabState = {
  plans: {
    data: [],
    searchQuery: "",
    statusFilter: "ALL",
    pageIndex: 0,
    pageSize: 10,
    totalCount: 0,
  },
  coverages: {
    data: [],
    searchQuery: "",
    statusFilter: "ALL",
    pageIndex: 0,
    pageSize: 10,
    totalCount: 0,
  },
  benefits: {
    data: [],
    searchQuery: "",
    statusFilter: "ALL",
    pageIndex: 0,
    pageSize: 10,
    totalCount: 0,
  },
};

const tabReducer = (state, action) => {
  switch (action.type) {
    case "SET_PLANS":
      return { ...state, plans: { ...state.plans, ...action.payload } };
    case "SET_COVERAGES":
      return { ...state, coverages: { ...state.coverages, ...action.payload } };
    case "SET_BENEFITS":
      return { ...state, benefits: { ...state.benefits, ...action.payload } };
    default:
      return state;
  }
};

const PlanCoverageViewer = () => {
  const { corpCode } = useParams();
  const navigate = useNavigate();
  const [tabState, dispatch] = React.useReducer(tabReducer, initialTabState);
  const [selectedCorp, setSelectedCorp] = useState(null);
  const [activeTab, setActiveTab] = useState("plan");
  const [loading, setLoading] = useState(false);
  const [isPlanViewOpen, setIsPlanViewOpen] = useState(false);
  const [viewPlanDetails, setViewPlanDetails] = useState(null);
  const [viewPlanLoading, setViewPlanLoading] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  const closeActionMenu = useCallback(() => {
    setOpenActionMenu(null);
    setActionMenuPosition(null);
  }, []);

  const handleActionMenuToggle = useCallback(
    (plan, event) => {
      event.stopPropagation();
      const rowKey = getPlanRowActionKey(plan);

      if (openActionMenu === rowKey) {
        closeActionMenu();
        return;
      }

      setOpenActionMenu(rowKey);
      setActionMenuPosition(getActionMenuPosition(event.currentTarget));
    },
    [closeActionMenu, openActionMenu]
  );

  useEffect(() => {
    if (!openActionMenu) {
      return undefined;
    }

    const handleViewportChange = () => closeActionMenu();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [closeActionMenu, openActionMenu]);

  const handleClosePlanView = useCallback(() => {
    setIsPlanViewOpen(false);
    setViewPlanDetails(null);
  }, []);

  const handleViewPlan = useCallback(async (plan) => {
    setIsPlanViewOpen(true);
    setViewPlanLoading(true);
    setViewPlanDetails(null);

    try {
      const response = await axios.get(
        `${BASE_URL}/plan-coverages/plans/v2/${plan.plan_code}`,
        { headers: getAuthHeaders() }
      );
      setViewPlanDetails({
        ...response.data,
        plan: {
          ...(response.data.plan || {}),
          corp_name:
            response.data.plan?.corp_name ||
            selectedCorp?.label ||
            plan.corp_name ||
            "",
        },
      });
    } catch (error) {
      toast.error(
        "Failed to load plan details: " +
          (error.response?.data?.message || error.message)
      );
      setIsPlanViewOpen(false);
    } finally {
      setViewPlanLoading(false);
    }
  }, [selectedCorp]);

  // Function to toggle plan status with SweetAlert2 confirmation
  const togglePlanStatus = async (corpCode, planCode, currentStatus) => {
    const newStatus = currentStatus == 1 ? 0 : 1;
    const actionText = newStatus == 1 ? "activate" : "deactivate";
    const result = await Swal.fire({
      title: `Are you sure you want to ${actionText} this plan?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, ${actionText}!`,
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        confirmButton:
          "bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 mr-2",
        cancelButton:
          "bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300",
      },
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `${BASE_URL}/plan-coverages/plans/${planCode}/status`,
          { corp_code: corpCode, status_flag: newStatus },
          { headers: getAuthHeaders() }
        );
        if (response.data.status == "success") {
          // fetchPlans();
          fetchPlansImmediate(); // Call immediately instead of debounced fetchPlans
          toast.success(`Plan ${actionText}d successfully!`);
        } else {
          toast.error(response.data.message || `Failed to ${actionText} plan.`);
        }
      } catch (error) {
        toast.error(`Failed to ${actionText} plan.`);
      }
    }
  };

  // Debounce Search
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const fetchCorpByCode = async (code) => {
    try {
      const res = await axios.get(`${BASE_URL}/plan-coverages`, {
        headers: getAuthHeaders(),
        params: { search_string: code, limit: 1 },
      });
      if (res.data.plan_coverages?.length > 0) {
        const corp = res.data.plan_coverages[0];
        return {
          value: corp.corp_code,
          label: `${corp.corp_code} - ${corp.corp_name} (${
            corp.actual_corp_name || corp.corp_name
          })`,
        };
      }
      return null;
    } catch (error) {
      toast.error("Failed to fetch corporate details.");
      return null;
    }
  };
  const loadInitialCorp = async () => {
    if (corpCode) {
      const corp = await fetchCorpByCode(corpCode);
      if (corp) {
        setSelectedCorp(corp);
      } else {
        toast.error("Corporate not found for provided code.");
      }
    } else {
      try {
        const res = await axios.get(`${BASE_URL}/plan-coverages`, {
          headers: getAuthHeaders(),
          params: { limit: 1 },
        });
        if (res.data.plan_coverages?.length > 0) {
          const firstCorp = res.data.plan_coverages[0];
          setSelectedCorp({
            value: firstCorp.corp_code,
            label: `${firstCorp.corp_code} - ${firstCorp.corp_name} (${
              firstCorp.actual_corp_name || firstCorp.corp_name
            })`,
          });
        }
      } catch (error) {
        toast.error("Failed to load initial corporate.");
      }
    }
  };
  useEffect(() => {
    loadInitialCorp();
  }, [corpCode]);

  const loadCorpOptions = async (inputValue) => {
    if (!inputValue) return [];
    try {
      const response = await axios.get(`${BASE_URL}/plan-coverages`, {
        headers: getAuthHeaders(),
        params: { search_string: inputValue, limit: 20 },
      });
      return response.data.plan_coverages.map((corp) => ({
        value: corp.corp_code,
        label: `${corp.corp_code} - ${corp.corp_name} (${
          corp.actual_corp_name || corp.corp_name
        })`,
      }));
    } catch (error) {
      toast.error("Failed to search corporates.");
      return [];
    }
  };

  const fetchPlans = useCallback(
    debounce(async () => {
      if (!selectedCorp) return;
      setLoading(true);
      try {
        const res = await axios.get(
          `${BASE_URL}/plan-coverages/${selectedCorp.value}/plans`,
          {
            headers: getAuthHeaders(),
            params: {
              search_string: tabState.plans.searchQuery,
              status_flag:
                tabState.plans.statusFilter !== "ALL"
                  ? tabState.plans.statusFilter
                  : undefined,
              limit_offset: tabState.plans.pageIndex * tabState.plans.pageSize,
              limit: tabState.plans.pageSize,
            },
          }
        );

        console.log("fetchPlans response:", res.data);

        dispatch({
          type: "SET_PLANS",
          payload: {
            data: res.data.plans || [],
            totalCount: res.data.total_count || 0,
          },
        });
      } catch (error) {
        toast.error("Failed to load plans.");
      } finally {
        setLoading(false);
      }
    }, 300),
    [
      selectedCorp,
      tabState.plans.searchQuery,
      tabState.plans.statusFilter,
      tabState.plans.pageIndex,
      tabState.plans.pageSize,
    ]
  );

  const fetchPlansImmediate = async () => {
    if (!selectedCorp) {
      // alert("[ERROR!] - !selectedCOrp: ", selectedCorp);
      loadInitialCorp();
      return;
    }

    // alert("[OK!] - selectedCOrp: ", selectedCorp);

    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/plan-coverages/${selectedCorp.value}/plans`,
        {
          headers: getAuthHeaders(),
          params: {
            search_string: tabState.plans.searchQuery,
            status_flag:
              tabState.plans.statusFilter !== "ALL"
                ? tabState.plans.statusFilter
                : undefined,
            limit_offset: tabState.plans.pageIndex * tabState.plans.pageSize,
            limit: tabState.plans.pageSize,
          },
        }
      );

      dispatch({
        type: "SET_PLANS",
        payload: {
          data: res.data.plans || [],
          totalCount: res.data.total_count || 0,
        },
      });
    } catch (error) {
      toast.error("Failed to load plans.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCoverages = useCallback(
    debounce(async () => {
      if (!selectedCorp) return;
      setLoading(true);
      try {
        const res = await axios.get(
          `${BASE_URL}/plan-coverages/${selectedCorp.value}/coverages`,
          {
            headers: getAuthHeaders(),
            params: {
              search_string: tabState.coverages.searchQuery,
              status_flag:
                tabState.coverages.statusFilter !== "ALL"
                  ? tabState.coverages.statusFilter.toLowerCase()
                  : undefined,
              limit_offset:
                tabState.coverages.pageIndex * tabState.coverages.pageSize,
              limit: tabState.coverages.pageSize,
            },
          }
        );
        dispatch({
          type: "SET_COVERAGES",
          payload: {
            data: res.data.coverages || [],
            totalCount: res.data.total_count || 0,
          },
        });
      } catch (error) {
        toast.error("Failed to load coverages.");
      } finally {
        setLoading(false);
      }
    }, 300),
    [
      selectedCorp,
      tabState.coverages.searchQuery,
      tabState.coverages.statusFilter,
      tabState.coverages.pageIndex,
      tabState.coverages.pageSize,
    ]
  );

  const fetchBenefits = useCallback(
    debounce(async () => {
      if (!selectedCorp) return;
      setLoading(true);
      try {
        const res = await axios.get(
          `${BASE_URL}/plan-coverages/${selectedCorp.value}/benefits`,
          {
            headers: getAuthHeaders(),
            params: {
              search_string: tabState.benefits.searchQuery,
              status:
                tabState.benefits.statusFilter !== "ALL"
                  ? tabState.benefits.statusFilter.toLowerCase()
                  : undefined,
              limit_offset:
                tabState.benefits.pageIndex * tabState.benefits.pageSize,
              limit: tabState.benefits.pageSize,
            },
          }
        );
        dispatch({
          type: "SET_BENEFITS",
          payload: {
            data: res.data.benefits || [],
            totalCount: res.data.total_count || 0,
          },
        });
      } catch (error) {
        toast.error("Failed to load benefits.");
      } finally {
        setLoading(false);
      }
    }, 300),
    [
      selectedCorp,
      tabState.benefits.searchQuery,
      tabState.benefits.statusFilter,
      tabState.benefits.pageIndex,
      tabState.benefits.pageSize,
    ]
  );

  useEffect(() => {
    if (!selectedCorp) return;
    if (activeTab == "plan") fetchPlans();
    else if (activeTab == "coverage") fetchCoverages();
    else if (activeTab == "benefits") fetchBenefits();
  }, [selectedCorp, activeTab, fetchPlans, fetchCoverages, fetchBenefits]);

  const handleEditPlan = useCallback(
    (plan) => {
      navigate(
        `/plan-and-coverage-benefits-setup/edit/${plan.corp_code}/${plan.plan_code}`
      );
    },
    [navigate]
  );

  const getPlanRowActions = (plan) => [
    {
      key: "view",
      label: "View details",
      icon: faEye,
      color: "text-sky-600",
      onClick: () => handleViewPlan(plan),
    },
    {
      key: "edit",
      label: "Edit plan",
      icon: faEdit,
      color: "text-emerald-600",
      onClick: () => handleEditPlan(plan),
    },
    {
      key: "toggle",
      label: plan.status_flag == 1 ? "Deactivate plan" : "Activate plan",
      icon: plan.status_flag == 1 ? faToggleOff : faToggleOn,
      color: plan.status_flag == 1 ? "text-red-600" : "text-green-600",
      onClick: () =>
        togglePlanStatus(plan.corp_code, plan.plan_code, plan.status_flag),
    },
  ];

  const planColumns = useMemo(
    () => [
      { Header: "Corp Code", accessor: "corp_code" },
      { Header: "Plan Code", accessor: "plan_code" },
      {
        Header: "Plan Name",
        accessor: "plan_name",
        Cell: ({ value }) => <PlanBadge value={value} />
      },
      {
        // Header: "Plan Type",
        Header: () => (
          <div className="flex items-center space-x-1">
            <span>Plan Type</span>
            <PlanTypeInfoTooltip_v2 />
          </div>
        ),
        accessor: "plan_type",
        Cell: ({ value }) => {
          const map = {
            p: { label: "P - Principal", color: "blue" },
            P: { label: "P - Principal", color: "blue" },
            d: { label: "D - Dependent", color: "orange" },
            D: { label: "D - Dependent", color: "orange" },
            b: { label: "B - Both", color: "purple" },
            B: { label: "B - Both", color: "purple" },
          };

          const plan = map[value] || { label: "Unknown", color: "gray" };

          return <Badge color={plan.color}>{plan.label}</Badge>;
        },
      },

      { Header: "Effective Date", accessor: "effective_date" },
      { Header: "Expiry Date", accessor: "expiry_date" },
      {
        Header: "Status",
        accessor: "status_flag",
        Cell: ({ value }) => (
          <Badge color={value == 1 ? "green" : "red"}>
            {value == 1 ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="relative">
            <button
              type="button"
              aria-label={`Actions for plan ${row.original.plan_code || ""}`}
              onClick={(event) => handleActionMenuToggle(row.original, event)}
              className={clsx(
                "inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold shadow-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
                openActionMenu === getPlanRowActionKey(row.original)
                  ? "border-blue-400 bg-blue-50 text-blue-700 shadow-blue-100"
                  : "border-gray-300 bg-white text-gray-700 hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
              )}
            >
              <FontAwesomeIcon icon={faEllipsisVertical} className="text-gray-500" />
              <span>Actions</span>
            </button>
            {openActionMenu === getPlanRowActionKey(row.original) &&
              actionMenuPosition &&
              typeof document !== "undefined" &&
              createPortal(
                <>
                  <button
                    type="button"
                    aria-label="Close plan actions menu"
                    className="fixed inset-0 z-[9998] cursor-default bg-transparent"
                    onClick={closeActionMenu}
                  />
                  <div
                    className="fixed z-[9999] w-72 overflow-visible"
                    style={{
                      top: `${actionMenuPosition.top}px`,
                      left: `${actionMenuPosition.left}px`,
                    }}
                  >
                    <div
                      className={clsx(
                        "absolute right-6 h-3 w-3 rotate-45 border bg-white",
                        actionMenuPosition.placement === "top"
                          ? "-bottom-1.5 border-l-0 border-t-0 border-gray-200"
                          : "-top-1.5 border-b-0 border-r-0 border-gray-200"
                      )}
                    />
                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl shadow-slate-900/15 ring-1 ring-black/5">
                      <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Plan Actions
                        </p>
                      </div>
                      <div className="space-y-1 p-2">
                        {getPlanRowActions(row.original).map((action) => (
                          <button
                            key={action.key}
                            type="button"
                            onClick={() => {
                              action.onClick();
                              closeActionMenu();
                            }}
                            className="group flex w-full items-center justify-between rounded-lg border border-transparent px-3 py-2.5 text-left text-sm text-gray-700 transition-all duration-150 hover:translate-x-0.5 hover:border-blue-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-blue-700 hover:shadow-sm"
                          >
                            <span className="flex min-w-0 items-center">
                              <span className="mr-3 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 transition-all duration-150 group-hover:bg-white group-hover:shadow-sm">
                                <FontAwesomeIcon icon={action.icon} className={`text-sm ${action.color}`} />
                              </span>
                              <span className="truncate font-medium">{action.label}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </>,
                document.body
              )}
          </div>
        ),
      },
    ],
    [
      actionMenuPosition,
      closeActionMenu,
      handleActionMenuToggle,
      handleEditPlan,
      handleViewPlan,
      openActionMenu,
    ]
  );

  const coverageColumns = useMemo(
    () => [
      { Header: "Plan Code", accessor: "plan_code" },
      {
        Header: "Plan Name",
        accessor: "plan_name",
        Cell: ({ value }) => <PlanBadge value={value} />
      },
      { Header: "Coverage Code", accessor: "coverage_code" },
      {
        Header: "Amount Limit",
        accessor: "amount_limit",
        Cell: ({ value }) => value || "N/A",
      },
      { Header: "Count Limit", accessor: "count_limit" },
      {
        Header: "Status",
        accessor: "status_flag",
        Cell: ({ value }) => (
          <Badge color={value == "active" ? "green" : "red"}>{value}</Badge>
        ),
      },
    ],
    []
  );

  const benefitColumns = useMemo(
    () => [
      { Header: "Plan Code", accessor: "plan_code" },

      {
        Header: "Plan Name",
        accessor: "plan_name",
        Cell: ({ value }) => <PlanBadge value={value} />
      },

      { Header: "Coverage Code", accessor: "coverage_code" },
      { Header: "Benefit Code", accessor: "benefit_code" },
      { Header: "Benefit Desc", accessor: "benefit_desc" },
      {
        Header: "Basic Limit",
        accessor: "basic_limit",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "Major Limit",
        accessor: "major_limit",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => (
          <Badge color={value == "active" ? "green" : "red"}>{value}</Badge>
        ),
      },
    ],
    []
  );

  const {
    getTableProps: getPlanTableProps,
    getTableBodyProps: getPlanTableBodyProps,
    headerGroups: planHeaderGroups,
    page: planPage,
    prepareRow: preparePlanRow,
    previousPage: planPreviousPage,
    nextPage: planNextPage,
    canPreviousPage: planCanPreviousPage,
    canNextPage: planCanNextPage,
    pageCount: planPageCount,
    gotoPage: planGotoPage,
    setPageSize: planSetPageSize,
    state: { pageIndex: planPageIndex },
  } = useTable(
    {
      columns: planColumns,
      data: tabState.plans.data,
      initialState: {
        pageIndex: tabState.plans.pageIndex,
        pageSize: tabState.plans.pageSize,
      },
      manualPagination: true,
      pageCount: Math.ceil(tabState.plans.totalCount / tabState.plans.pageSize),
    },
    usePagination
  );

  const {
    getTableProps: getCoverageTableProps,
    getTableBodyProps: getCoverageTableBodyProps,
    headerGroups: coverageHeaderGroups,
    page: coveragePage,
    prepareRow: prepareCoverageRow,
    previousPage: coveragePreviousPage,
    nextPage: coverageNextPage,
    canPreviousPage: coverageCanPreviousPage,
    canNextPage: coverageCanNextPage,
    pageCount: coveragePageCount,
    gotoPage: coverageGotoPage,
    setPageSize: coverageSetPageSize,
    state: { pageIndex: coveragePageIndex },
  } = useTable(
    {
      columns: coverageColumns,
      data: tabState.coverages.data,
      initialState: {
        pageIndex: tabState.coverages.pageIndex,
        pageSize: tabState.coverages.pageSize,
      },
      manualPagination: true,
      pageCount: Math.ceil(
        tabState.coverages.totalCount / tabState.coverages.pageSize
      ),
    },
    usePagination
  );

  const {
    getTableProps: getBenefitTableProps,
    getTableBodyProps: getBenefitTableBodyProps,
    headerGroups: benefitHeaderGroups,
    page: benefitPage,
    prepareRow: prepareBenefitRow,
    previousPage: benefitPreviousPage,
    nextPage: benefitNextPage,
    canPreviousPage: benefitCanPreviousPage,
    canNextPage: benefitCanNextPage,
    pageCount: benefitPageCount,
    gotoPage: benefitGotoPage,
    setPageSize: benefitSetPageSize,
    state: { pageIndex: benefitPageIndex },
  } = useTable(
    {
      columns: benefitColumns,
      data: tabState.benefits.data,
      initialState: {
        pageIndex: tabState.benefits.pageIndex,
        pageSize: tabState.benefits.pageSize,
      },
      manualPagination: true,
      pageCount: Math.ceil(
        tabState.benefits.totalCount / tabState.benefits.pageSize
      ),
    },
    usePagination
  );

  useEffect(() => {
    dispatch({ type: "SET_PLANS", payload: { pageIndex: planPageIndex } });
  }, [planPageIndex]);

  useEffect(() => {
    dispatch({
      type: "SET_COVERAGES",
      payload: { pageIndex: coveragePageIndex },
    });
  }, [coveragePageIndex]);

  useEffect(() => {
    dispatch({
      type: "SET_BENEFITS",
      payload: { pageIndex: benefitPageIndex },
    });
  }, [benefitPageIndex]);

  const renderPagination = (tab) => {
    const tabMap = {
      plan: "plans",
      coverage: "coverages",
      benefits: "benefits",
    };
    const stateKey = tabMap[tab];
    if (!stateKey || !tabState[stateKey]) {
      console.error(`Invalid tab: ${tab}`);
      return null;
    }
    const { pageIndex, pageSize, totalCount } = tabState[stateKey];
    const pageCount = Math.ceil(totalCount / pageSize) || 1;
    const gotoPage =
      tab == "plan"
        ? planGotoPage
        : tab == "coverage"
        ? coverageGotoPage
        : benefitGotoPage;
    const previousPage =
      tab == "plan"
        ? planPreviousPage
        : tab == "coverage"
        ? coveragePreviousPage
        : benefitPreviousPage;
    const nextPage =
      tab == "plan"
        ? planNextPage
        : tab == "coverage"
        ? coverageNextPage
        : benefitNextPage;
    const canPreviousPage =
      tab == "plan"
        ? planCanPreviousPage
        : tab == "coverage"
        ? coverageCanPreviousPage
        : benefitCanPreviousPage;
    const canNextPage =
      tab == "plan"
        ? planCanNextPage
        : tab == "coverage"
        ? coverageCanNextPage
        : benefitCanNextPage;
    const setPageSize =
      tab == "plan"
        ? planSetPageSize
        : tab == "coverage"
        ? coverageSetPageSize
        : benefitSetPageSize;
    return (
      <div className="flex items-center justify-between px-6 py-3 bg-gray-50 text-sm text-gray-700 border-t border-gray-200">
        <div>
          Showing {pageIndex * pageSize + 1} to{" "}
          {Math.min((pageIndex + 1) * pageSize, totalCount)} of {totalCount}{" "}
          {stateKey}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
            className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50"
            title="First Page"
          >
            <ChevronDoubleLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          <button
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50"
            title="Previous Page"
          >
            <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          <span className="px-2 py-1 font-medium">
            {pageIndex + 1} of {pageCount}
          </span>
          <button
            onClick={() => nextPage()}
            disabled={!canNextPage}
            className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50"
            title="Next Page"
          >
            <ChevronRightIcon className="h-5 w-5 text-gray-600" />
          </button>
          <button
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
            className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50"
            title="Last Page"
          >
            <ChevronDoubleRightIcon className="h-5 w-5 text-gray-600" />
          </button>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              dispatch({
                type:
                  tab == "plan"
                    ? "SET_PLANS"
                    : tab == "coverage"
                    ? "SET_COVERAGES"
                    : "SET_BENEFITS",
                payload: { pageSize: Number(e.target.value), pageIndex: 0 },
              });
            }}
            className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          >
            {[10, 20, 50].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full max-w-1xl p-4">
      <div className="mb-4 rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-white/10 p-2 ring-1 ring-white/20 backdrop-blur">
              <FontAwesomeIcon icon={faShieldAlt} className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-base font-semibold">
                Plan & Coverage Edit & Viewer
              </h1>
            </div>
          </div>
        </div>
        <div className="p-6 bg-white rounded-xl">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Corporate
            </label>
            <AsyncSelect
              cacheOptions
              defaultOptions
              loadOptions={loadCorpOptions}
              onChange={setSelectedCorp}
              value={selectedCorp}
              placeholder="Search by code, name, or actual name..."
              isClearable
              className="text-sm"
              classNamePrefix="react-select"
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary: "#3b82f6",
                  primary25: "#dbeafe",
                  neutral0: "#ffffff",
                  neutral20: "#d1d5db",
                },
              })}
            />
          </div>
          {selectedCorp && (
            <>
              <div className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-6">
                {["plan", "coverage", "benefits"].map((tab) => (
                  <motion.button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={clsx(
                      "flex-1 py-2.5 px-4 text-sm font-medium rounded-lg transition-colors",
                      activeTab == tab
                        ? "bg-white shadow-md text-blue-600"
                        : "text-gray-600 hover:bg-gray-200"
                    )}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {tab == "plan"
                      ? "Plans"
                      : tab == "coverage"
                      ? "Coverages"
                      : "Benefits"}
                  </motion.button>
                ))}
              </div>
              {activeTab == "plan" && (
                <div className="mb-6 flex items-center gap-4">
                  <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={tabState.plans.searchQuery}
                      onChange={(e) =>
                        dispatch({
                          type: "SET_PLANS",
                          payload: {
                            searchQuery: e.target.value,
                            pageIndex: 0,
                          },
                        })
                      }
                      placeholder="Search plans..."
                      className="w-full rounded-md border border-gray-300 pl-10 pr-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                    />
                  </div>
                  <select
                    value={tabState.plans.statusFilter}
                    onChange={(e) =>
                      dispatch({
                        type: "SET_PLANS",
                        payload: { statusFilter: e.target.value, pageIndex: 0 },
                      })
                    }
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="ALL">All Status</option>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              )}
              {activeTab == "coverage" && (
                <div className="mb-6 flex items-center gap-4">
                  <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={tabState.coverages.searchQuery}
                      onChange={(e) =>
                        dispatch({
                          type: "SET_COVERAGES",
                          payload: {
                            searchQuery: e.target.value,
                            pageIndex: 0,
                          },
                        })
                      }
                      placeholder="Search coverages..."
                      className="w-full rounded-md border border-gray-300 pl-10 pr-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                    />
                  </div>
                  <select
                    value={tabState.coverages.statusFilter}
                    onChange={(e) =>
                      dispatch({
                        type: "SET_COVERAGES",
                        payload: { statusFilter: e.target.value, pageIndex: 0 },
                      })
                    }
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="ALL">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}
              {activeTab == "benefits" && (
                <div className="mb-6 flex items-center gap-4">
                  <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={tabState.benefits.searchQuery}
                      onChange={(e) =>
                        dispatch({
                          type: "SET_BENEFITS",
                          payload: {
                            searchQuery: e.target.value,
                            pageIndex: 0,
                          },
                        })
                      }
                      placeholder="Search benefits..."
                      className="w-full rounded-md border border-gray-300 pl-10 pr-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                    />
                  </div>
                  <select
                    value={tabState.benefits.statusFilter}
                    onChange={(e) =>
                      dispatch({
                        type: "SET_BENEFITS",
                        payload: { statusFilter: e.target.value, pageIndex: 0 },
                      })
                    }
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="ALL">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}
              <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                {loading ? (
                  <Skeleton count={10} height={40} className="mb-2" />
                ) : (
                  <>
                    {activeTab == "plan" && (
                      <>
                        {planPage.length > 0 ? (
                          <table
                            {...getPlanTableProps()}
                            className="min-w-full divide-y divide-gray-200"
                          >
                            <thead className="bg-gray-50">
                              {planHeaderGroups.map((headerGroup) => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                  {headerGroup.headers.map((column) => (
                                    <th
                                      {...column.getHeaderProps()}
                                      className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                                    >
                                      {column.render("Header")}
                                    </th>
                                  ))}
                                </tr>
                              ))}
                            </thead>
                            <tbody
                              {...getPlanTableBodyProps()}
                              className="bg-white divide-y divide-gray-200"
                            >
                              {planPage.map((row) => {
                                preparePlanRow(row);
                                return (
                                  <tr
                                    {...row.getRowProps()}
                                    className="hover:bg-gray-50 transition-colors"
                                  >
                                    {row.cells.map((cell) => (
                                      <td
                                        {...cell.getCellProps()}
                                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                                      >
                                        {cell.render("Cell")}
                                      </td>
                                    ))}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        ) : (
                          <EmptyState
                            title="No plans found"
                            subtitle="Try adjusting your search or filters."
                          />
                        )}
                        {planPage.length > 0 && renderPagination("plan")}
                      </>
                    )}
                    {activeTab == "coverage" && (
                      <>
                        {coveragePage.length > 0 ? (
                          <table
                            {...getCoverageTableProps()}
                            className="min-w-full divide-y divide-gray-200"
                          >
                            <thead className="bg-gray-50">
                              {coverageHeaderGroups.map((headerGroup) => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                  {headerGroup.headers.map((column) => (
                                    <th
                                      {...column.getHeaderProps()}
                                      className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                                    >
                                      {column.render("Header")}
                                    </th>
                                  ))}
                                </tr>
                              ))}
                            </thead>
                            <tbody
                              {...getCoverageTableBodyProps()}
                              className="bg-white divide-y divide-gray-200"
                            >
                              {coveragePage.map((row) => {
                                prepareCoverageRow(row);
                                return (
                                  <tr
                                    {...row.getRowProps()}
                                    className="hover:bg-gray-50 transition-colors"
                                  >
                                    {row.cells.map((cell) => (
                                      <td
                                        {...cell.getCellProps()}
                                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                                      >
                                        {cell.render("Cell")}
                                      </td>
                                    ))}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        ) : (
                          <EmptyState
                            title="No coverages found"
                            subtitle="Try adjusting your search or filters."
                          />
                        )}
                        {coveragePage.length > 0 &&
                          renderPagination("coverage")}
                      </>
                    )}
                    {activeTab == "benefits" && (
                      <>
                        {benefitPage.length > 0 ? (
                          <table
                            {...getBenefitTableProps()}
                            className="min-w-full divide-y divide-gray-200"
                          >
                            <thead className="bg-gray-50">
                              {benefitHeaderGroups.map((headerGroup) => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                  {headerGroup.headers.map((column) => (
                                    <th
                                      {...column.getHeaderProps()}
                                      className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                                    >
                                      {column.render("Header")}
                                    </th>
                                  ))}
                                </tr>
                              ))}
                            </thead>
                            <tbody
                              {...getBenefitTableBodyProps()}
                              className="bg-white divide-y divide-gray-200"
                            >
                              {benefitPage.map((row) => {
                                prepareBenefitRow(row);
                                return (
                                  <tr
                                    {...row.getRowProps()}
                                    className="hover:bg-gray-50 transition-colors"
                                  >
                                    {row.cells.map((cell) => (
                                      <td
                                        {...cell.getCellProps()}
                                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                                      >
                                        {cell.render("Cell")}
                                      </td>
                                    ))}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        ) : (
                          <EmptyState
                            title="No benefits found"
                            subtitle="Try adjusting your search or filters."
                          />
                        )}
                        {benefitPage.length > 0 && renderPagination("benefits")}
                      </>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          <PlanViewModal
            isOpen={isPlanViewOpen}
            loading={viewPlanLoading}
            details={viewPlanDetails}
            onClose={handleClosePlanView}
          />

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar
          />
        </div>
      </div>
    </div>
  );
};

export default PlanCoverageViewer;
